import { authHeader } from "./token";
import { tableCodeHeader } from "./tableAccess";
import { BASE_API, PAYMENT_STATUS } from "../utils/constants";

export async function createPaymentApi(paymentData, token) {
  try {
    const url = `${BASE_API}/api/payments/`;
    const params = {
      method: "POST",
      headers: {
        ...authHeader(token),
        ...tableCodeHeader(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(paymentData),
    };

    const response = await fetch(url, params);
    const result = await response.json();
    return result;
  } catch (error) {
    throw error;
  }
}

export async function getPaymentByTableApi(idTable, token) {
  try {
    const tableFilter = `table=${idTable}`;
    const statusFilter = `statusPayment=${PAYMENT_STATUS.PENDING}`;
    const orderingFilter = "ordering=-created_at";

    const url = `${BASE_API}/api/payments/?${tableFilter}&${statusFilter}&${orderingFilter}`;
    const params = {
      headers: {
        ...authHeader(token),
        ...tableCodeHeader(),
        "Content-Type": "application/json",
      },
    };
    const response = await fetch(url, params);
    const result = await response.json();
    return result;
  } catch (error) {
    throw error;
  }
}

// Cuentas pendientes de cobro de todas las mesas (Caja → Cobrar). Solo staff.
export async function getPendingPaymentsApi(token) {
  const url = `${BASE_API}/api/payments/?statusPayment=${PAYMENT_STATUS.PENDING}`;
  const response = await fetch(url, { headers: authHeader(token) });
  if (!response.ok) throw new Error("No se pudieron cargar las cuentas pendientes.");
  return response.json();
}

export async function closePaymentApi(
  idPayment,
  operationCode,
  token,
  cashReceived = null,
  parts = null,
  paymentType = null
) {
  try {
    const url = `${BASE_API}/api/payments/${idPayment}/`;
    const params = {
      method: "PATCH",
      headers: {
        ...authHeader(token),
        ...tableCodeHeader(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        statusPayment: PAYMENT_STATUS.PAID,
        // Solo Yape/Plin: código de operación copiado de la app del cliente.
        ...(operationCode ? { operation_code: operationCode } : {}),
        // Solo efectivo: cuánto entregó el cliente (vuelto en el ticket).
        ...(cashReceived != null ? { cash_received: cashReceived } : {}),
        // Pago mixto: [{method, amount, operation_code}], suman el total.
        ...(parts ? { paymentType: "MIXED", parts } : {}),
        // Cuenta avisada como "Mixto" que al final se cobra en una sola forma.
        ...(!parts && paymentType ? { paymentType } : {}),
      }),
    };
    const response = await fetch(url, params);
    // Un pago mixto que no cuadra lo rechaza el backend: no cerrar la mesa.
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        (Array.isArray(body.parts) ? body.parts[0] : body.parts) || "No se pudo registrar el pago."
      );
    }
  } catch (error) {
    throw error;
  }
}

export async function getPaymentsInRangeApi(dateFrom, dateTo, token) {
  try {
    const statusFilter = `statusPayment=${PAYMENT_STATUS.PAID}`;
    const dateFromFilter = dateFrom ? `&date_from=${dateFrom}` : "";
    const dateToFilter = dateTo ? `&date_to=${dateTo}` : "";
    const orderingFilter = "ordering=-created_date,table_number,-created_at";

    const url = `${BASE_API}/api/payments/?${statusFilter}${dateFromFilter}${dateToFilter}&${orderingFilter}`;

    const response = await fetch(url, { headers: { ...authHeader(token), ...tableCodeHeader() } });
    const result = await response.json();
    return result;
  } catch (error) {
    throw error;
  }
}

export async function getLossesInRangeApi(dateFrom, dateTo, token) {
  try {
    const statusFilter = `statusPayment=${PAYMENT_STATUS.PAID}`;
    const typeFilter = "paymentType=LOSS";
    const dateFromFilter = dateFrom ? `&date_from=${dateFrom}` : "";
    const dateToFilter = dateTo ? `&date_to=${dateTo}` : "";
    const orderingFilter = "ordering=-created_date,table_number,-created_at";

    const url = `${BASE_API}/api/payments/?${statusFilter}&${typeFilter}${dateFromFilter}${dateToFilter}&${orderingFilter}`;

    const response = await fetch(url, { headers: { ...authHeader(token), ...tableCodeHeader() } });
    const result = await response.json();
    return result;
  } catch (error) {
    throw error;
  }
}

export async function getPaymentsApi(sortBy = "date", dateFrom, dateTo, token) {
  try {
    const paymentFilter = `statusPayment=${PAYMENT_STATUS.PAID}`;
    const ordering =
      sortBy === "table"
        ? "table_number,-created_date,-created_at"
        : "-created_at,table_number";
    const orderingFilter = `ordering=${ordering}`;
    const dateFromFilter = dateFrom ? `&date_from=${dateFrom}` : "";
    const dateToFilter = dateTo ? `&date_to=${dateTo}` : "";

    const url = `${BASE_API}/api/payments/?${paymentFilter}${dateFromFilter}${dateToFilter}&${orderingFilter}`;

    const response = await fetch(url, { headers: { ...authHeader(token), ...tableCodeHeader() } });
    const result = await response.json();
    return result;
  } catch (error) {
    throw error;
  }
}

// Anular una venta cobrada (solo Admin/Súper Usuario, el backend lo exige).
export async function voidPaymentApi(idPayment, reason, token) {
  const url = `${BASE_API}/api/payments/${idPayment}/void/`;
  const response = await fetch(url, {
    method: "POST",
    headers: { ...authHeader(token), "Content-Type": "application/json" },
    body: JSON.stringify({ reason }),
  });
  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(`No se pudo anular la venta: ${errorBody}`);
  }
  return await response.json();
}

export async function getVoidedPaymentsApi(dateFrom, dateTo, token) {
  const url = `${BASE_API}/api/payments/?statusPayment=${PAYMENT_STATUS.VOID}&date_from=${dateFrom}&date_to=${dateTo}&ordering=-voided_at`;
  const response = await fetch(url, { headers: { ...authHeader(token) } });
  if (!response.ok) throw new Error(`No se pudieron leer las ventas anuladas (${response.status})`);
  return await response.json();
}

// Descuento o cortesía sobre una cuenta pendiente: { type: "AMOUNT"|"PERCENT",
// value, reason }. value 0 lo quita. Devuelve el pago con el total nuevo.
export async function discountPaymentApi(idPayment, discount, token) {
  const url = `${BASE_API}/api/payments/${idPayment}/discount/`;
  const response = await fetch(url, {
    method: "POST",
    headers: { ...authHeader(token), "Content-Type": "application/json" },
    body: JSON.stringify(discount),
  });
  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(`No se pudo aplicar el descuento: ${errorBody}`);
  }
  return await response.json();
}

export async function getDiscountedPaymentsApi(dateFrom, dateTo, token) {
  const url = `${BASE_API}/api/payments/?statusPayment=${PAYMENT_STATUS.PAID}&has_discount=true&date_from=${dateFrom}&date_to=${dateTo}&ordering=-created_at`;
  const response = await fetch(url, { headers: { ...authHeader(token) } });
  if (!response.ok) throw new Error(`No se pudieron leer los descuentos (${response.status})`);
  return await response.json();
}

// Corregir las personas de una cuenta ya generada (el número pasó de la mesa
// a la cuenta al crearla).
export async function setPaymentGuestsApi(idPayment, guests, token, waiter) {
  const response = await fetch(`${BASE_API}/api/payments/${idPayment}/`, {
    method: "PATCH",
    headers: { ...authHeader(token), "Content-Type": "application/json" },
    body: JSON.stringify({ guests, ...(waiter !== undefined ? { waiter_user: waiter } : {}) }),
  });
  if (!response.ok) throw new Error(`No se pudo guardar (${response.status})`);
  return await response.json();
}

// Propina declarada en la cuenta pendiente (fuera de la venta y del IGV).
export async function setPaymentTipApi(idPayment, tipCash, tipCard, token) {
  const response = await fetch(`${BASE_API}/api/payments/${idPayment}/`, {
    method: "PATCH",
    headers: { ...authHeader(token), "Content-Type": "application/json" },
    body: JSON.stringify({ tip_cash: tipCash, tip_card: tipCard }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(Object.values(body)[0]?.toString() || `No se pudo guardar (${response.status})`);
  }
  return await response.json();
}

export async function setPaymentCustomerApi(idPayment, customerId, token) {
  const response = await fetch(`${BASE_API}/api/payments/${idPayment}/`, {
    method: "PATCH",
    headers: { ...authHeader(token), "Content-Type": "application/json" },
    body: JSON.stringify({ customer: customerId }),
  });
  if (!response.ok) throw new Error(`No se pudo guardar (${response.status})`);
  return await response.json();
}

// Corregir canal/envío de una cuenta ya generada (el backend recalcula el total).
export async function setPaymentDeliveryApi(idPayment, channel, fee, token) {
  const response = await fetch(`${BASE_API}/api/payments/${idPayment}/`, {
    method: "PATCH",
    headers: { ...authHeader(token), "Content-Type": "application/json" },
    body: JSON.stringify({ delivery_channel: channel, delivery_fee: fee }),
  });
  if (!response.ok) throw new Error(`No se pudo guardar (${response.status})`);
  return await response.json();
}
